import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/hooks/use-auth';

const schema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Enter a valid email')
    .refine((v) => v.toLowerCase().endsWith('@kim.cc'), {
      message: 'Use your Kim work email (name@kim.cc).'
    }),
  password: z.string().min(1, 'Password is required')
});

type FormData = z.infer<typeof schema>;

export function LoginForm() {
  const navigate = useNavigate();
  const { signIn, isLoading, error } = useAuth();
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const onSubmit = async (data: FormData) => {
    const ok = await signIn(data.email, data.password);
    if (ok) navigate('/accounts');
  };

  return (
    <Card>
      <CardContent className='pt-6'>
        <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
          <div className='space-y-2'>
            <Label htmlFor='email'>Work email</Label>
            <Input
              id='email'
              type='email'
              placeholder='you@kim.cc'
              autoComplete='email'
              {...register('email')}
            />
            {errors.email && (
              <p className='text-xs text-destructive'>{errors.email.message}</p>
            )}
          </div>
          <div className='space-y-2'>
            <Label htmlFor='password'>Password</Label>
            <Input
              id='password'
              type='password'
              autoComplete='current-password'
              {...register('password')}
            />
            {errors.password && (
              <p className='text-xs text-destructive'>
                {errors.password.message}
              </p>
            )}
          </div>
          {error && (
            <div className='rounded-md bg-destructive/10 p-3 text-xs text-destructive'>
              {error}
            </div>
          )}
          <Button type='submit' className='w-full' disabled={isLoading}>
            {isLoading ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
